"use strict";
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.setSelectedPrediction = exports.setAlertSubject = exports.setThresholdOptimization = exports.resetSessionThreshold = exports.updateSessionThreshold = exports.onModelTrainingResultRejected = exports.onModelTrainingResultPending = exports.onModelTrainingResultFulfilled = void 0;
var toolkit_1 = require("@reduxjs/toolkit");
var ModelMetrics_1 = require("../../components/model-results/type/ModelMetrics");
var ConfusionMatricesUtils_1 = require("../../util/ConfusionMatricesUtils");
var MapperUtils_1 = require("../../util/MapperUtils");
var TestIds_1 = require("../../util/TestIds");
var Utils_1 = require("../../util/Utils");
var ValidatorUtils_1 = require("../../util/ValidatorUtils");
var ModelResultThunk_1 = require("../thunk/ModelResultThunk");
var PredictiveModelThunk_1 = require("../thunk/PredictiveModelThunk");
var initialState = {
    // general
    type: null,
    jobId: null,
    error: '',
    status: Utils_1.REDUX_STATUS.idle,
    // alerts
    alerts: [],
    isImprovement: false,
    qualityCheckPass: true,
    // analyses
    modelOutcome: null,
    modelFields: [],
    modelMetadata: null,
    // coefficients
    modelCoefficients: null,
    // input profile
    modelCorrelation: [],
    // model metrics
    modelMetrics: null,
    sessionThreshold: null,
    originalThreshold: null,
    thresholdStatus: ModelMetrics_1.THRESHOLD_STATUS.IDLE,
    currentThresholdClassification: null,
    confusionMatricesClassification: [],
    thresholdOptimization: ModelMetrics_1.ThresholdOptimizeCategory.CUSTOM,
    // outcome dist
    modelOutcomeDistribution: [],
    // predict
    predictions: [],
    selectedPrediction: null,
    // residual
    residual: null,
    // wave
    waveQuery: null,
};
var getCurrentClassification = function (state) {
    var _a;
    return (_a = state.confusionMatricesClassification.find(function (n) { return n.threshold === state.sessionThreshold; })) !== null && _a !== void 0 ? _a : null;
};
var buildClassification = function (state, validation, story) {
    if ((0, Utils_1.isBinomialModel)(validation === null || validation === void 0 ? void 0 : validation.type)) {
        var binomialModelValidation = validation;
        state.confusionMatricesClassification = (0, ConfusionMatricesUtils_1.getConfusionMatricesClassification)(binomialModelValidation.confusionMatrices);
        var defaultThreshold = (story === null || story === void 0 ? void 0 : story.classificationThreshold)
            ? story.classificationThreshold.value
            : (0, ConfusionMatricesUtils_1.getDefaultThreshold)(binomialModelValidation.confusionMatrices, state.confusionMatricesClassification, ModelMetrics_1.ThresholdOptimizeCategory.ACCURACY);
        state.sessionThreshold = defaultThreshold !== null && defaultThreshold !== void 0 ? defaultThreshold : 0;
        state.originalThreshold = defaultThreshold !== null && defaultThreshold !== void 0 ? defaultThreshold : 0;
        state.currentThresholdClassification = getCurrentClassification(state);
    }
};
var buildSuccessAlertsResult = function (state, alerts, qualityCheckPass, fake) {
    if (!alerts) {
        return false;
    }
    state.alerts = (0, MapperUtils_1.unescapeAlerts)(alerts);
    state.qualityCheckPass = qualityCheckPass !== null && qualityCheckPass !== void 0 ? qualityCheckPass : true;
    state.isImprovement = fake !== null && fake !== void 0 ? fake : false;
    return true;
};
var buildSuccessAnalysesStoryResult = function (state, analysesStory, modelMetrics) {
    if (!analysesStory || !modelMetrics) {
        return false;
    }
    state.modelOutcome = analysesStory.outcome;
    state.modelFields = analysesStory.setup.fields;
    state.modelMetadata = (0, MapperUtils_1.getModelMetadata)(analysesStory, modelMetrics);
    return true;
};
/**
 * Coefficient is an optional field, so always return true
 */
var buildSuccessCoefficientResult = function (state, modelCoefficients) {
    if (modelCoefficients) {
        state.modelCoefficients = modelCoefficients;
    }
    return true;
};
var buildSuccessInputProfileResult = function (state, inputProfileResult) {
    if (!inputProfileResult) {
        return false;
    }
    state.modelCorrelation = (0, MapperUtils_1.mapInputProfileData)(inputProfileResult);
    return true;
};
var buildSuccessModelMetricsResult = function (state, modelMetrics, story) {
    if (!modelMetrics || !modelMetrics.training || !modelMetrics.crossValidation || !modelMetrics.validation) {
        return false;
    }
    buildClassification(state, modelMetrics.validation, story);
    state.type = modelMetrics.validation.type;
    state.modelMetrics = modelMetrics;
    return true;
};
var buildSuccessDataDistributionResult = function (state, dataDistributionResult, analysesStory) {
    if (!dataDistributionResult || !analysesStory) {
        return false;
    }
    var outcomeDistribution = (0, MapperUtils_1.mapDataDistributionData)(analysesStory, dataDistributionResult);
    state.modelOutcomeDistribution = outcomeDistribution || [];
    return true;
};
var buildSuccessPredictResult = function (state, predict) {
    if (!predict || !predict.predictions) {
        return false;
    }
    state.predictions = predict.predictions;
    return true;
};
var buildSuccessResidualResult = function (state, residual) {
    if (residual) {
        state.residual = residual;
    }
    return true;
};
var buildSuccessDataRowResult = function (state, dataRows) {
    if (!dataRows) {
        return false;
    }
    state.waveQuery = dataRows;
    return true;
};
var onModelTrainingResultFulfilled = function (state, payload) {
    try {
        var alertResult = payload.storyHistoryAlerts
            ? JSON.parse(payload.storyHistoryAlerts)
            : null;
        var storeyHistoryResult = payload.storyHistory ? JSON.parse(payload.storyHistory) : null;
        var modelResult = (payload === null || payload === void 0 ? void 0 : payload.validation) ? JSON.parse(payload.validation) : null;
        var coefficientsResult = payload.coefficients ? JSON.parse(payload.coefficients || '') : null;
        var inputProfileResult = payload.inputProfile ? JSON.parse(payload.inputProfile) : null;
        var dataDistributionResult = (payload === null || payload === void 0 ? void 0 : payload.dataDistribution) ? JSON.parse(payload.dataDistribution) : null;
        var predictResult = (payload === null || payload === void 0 ? void 0 : payload.predict) ? JSON.parse(payload.predict) : null;
        var residualResult = (payload === null || payload === void 0 ? void 0 : payload.residual) ? JSON.parse(payload.residual) : null;
        var waveQueryResult = (payload === null || payload === void 0 ? void 0 : payload.dataRows) ? JSON.parse(payload.dataRows) : null;
        var story = (payload === null || payload === void 0 ? void 0 : payload.story) ? JSON.parse(payload.story) : null;
        var success = true;
        success = success && buildSuccessAlertsResult(state, alertResult === null || alertResult === void 0 ? void 0 : alertResult.alerts, alertResult === null || alertResult === void 0 ? void 0 : alertResult.qualityCheckPass, alertResult === null || alertResult === void 0 ? void 0 : alertResult.fake);
        success = success && buildSuccessAnalysesStoryResult(state, storeyHistoryResult, modelResult === null || modelResult === void 0 ? void 0 : modelResult.metrics);
        success = success && buildSuccessCoefficientResult(state, coefficientsResult);
        success = success && buildSuccessInputProfileResult(state, inputProfileResult);
        success = success && buildSuccessModelMetricsResult(state, modelResult === null || modelResult === void 0 ? void 0 : modelResult.metrics, story);
        success = success && buildSuccessDataDistributionResult(state, dataDistributionResult === null || dataDistributionResult === void 0 ? void 0 : dataDistributionResult.results, storeyHistoryResult);
        success = success && buildSuccessPredictResult(state, predictResult);
        success = success && buildSuccessResidualResult(state, residualResult);
        success = success && buildSuccessDataRowResult(state, waveQueryResult === null || waveQueryResult === void 0 ? void 0 : waveQueryResult.results);
        if (success) {
            state.status = Utils_1.REDUX_STATUS.succeeded;
            state.error = null;
        }
        else {
            (0, exports.onModelTrainingResultRejected)(state);
        }
    }
    catch (_a) {
        (0, exports.onModelTrainingResultRejected)(state);
    }
};
exports.onModelTrainingResultFulfilled = onModelTrainingResultFulfilled;
var onModelTrainingResultPending = function (state) {
    state.status = Utils_1.REDUX_STATUS.loading;
};
exports.onModelTrainingResultPending = onModelTrainingResultPending;
var onModelTrainingResultRejected = function (state) {
    state.status = Utils_1.REDUX_STATUS.failed;
};
exports.onModelTrainingResultRejected = onModelTrainingResultRejected;
var buildRetrieveModelResultPromiseCases = function (builder) {
    builder.addCase(ModelResultThunk_1.fetchModelTrainingResult.fulfilled, function (state, action) {
        state.jobId = action.meta.arg.jobId;
        (0, exports.onModelTrainingResultFulfilled)(state, action.payload);
    });
    builder.addCase(ModelResultThunk_1.fetchModelTrainingResult.pending, function (state) {
        (0, exports.onModelTrainingResultPending)(state);
    });
    builder.addCase(ModelResultThunk_1.fetchModelTrainingResult.rejected, function (state) {
        state.jobId = null;
        (0, exports.onModelTrainingResultRejected)(state);
    });
};
var buildUpdateModelThresholdPromiseCases = function (builder) {
    builder.addCase(ModelResultThunk_1.updateModelThreshold.fulfilled, function (state, _a) {
        var _b;
        var payload = _a.payload;
        var thresholdValue = (_b = payload.classificationThreshold) === null || _b === void 0 ? void 0 : _b.value;
        if ((0, ValidatorUtils_1.isValidObject)(thresholdValue)) {
            state.sessionThreshold = thresholdValue;
            state.originalThreshold = thresholdValue;
        }
        state.thresholdStatus = ModelMetrics_1.THRESHOLD_STATUS.IDLE;
    });
    builder.addCase(ModelResultThunk_1.updateModelThreshold.pending, function (state) {
        state.thresholdStatus = ModelMetrics_1.THRESHOLD_STATUS.UPDATING;
    });
    builder.addCase(ModelResultThunk_1.updateModelThreshold.rejected, function (state) {
        state.thresholdStatus = ModelMetrics_1.THRESHOLD_STATUS.IDLE;
        (0, PredictiveModelThunk_1.showErrorToast)('Failed to set model threshold', TestIds_1.MODEL_RESULT_THRESHOLD_APPLY_ERROR_TEST_ID);
    });
};
var modelResultSlice = (0, toolkit_1.createSlice)({
    name: 'modelResult',
    initialState: initialState,
    reducers: {
        updateSessionThreshold: function (state, action) {
            state.thresholdStatus = ModelMetrics_1.THRESHOLD_STATUS.UPDATING;
            state.sessionThreshold = action.payload;
            state.currentThresholdClassification = getCurrentClassification(state);
            state.thresholdStatus = ModelMetrics_1.THRESHOLD_STATUS.IDLE;
        },
        resetSessionThreshold: function (state) {
            state.thresholdStatus = ModelMetrics_1.THRESHOLD_STATUS.UPDATING;
            state.sessionThreshold = state.originalThreshold;
            state.currentThresholdClassification = getCurrentClassification(state);
            state.thresholdOptimization = ModelMetrics_1.ThresholdOptimizeCategory.CUSTOM;
            state.thresholdStatus = ModelMetrics_1.THRESHOLD_STATUS.IDLE;
        },
        setThresholdOptimization: function (state, action) {
            state.thresholdOptimization = action.payload;
        },
        setAlertSubject: function (state, action) {
            var alerts = state.alerts;
            var alert = alerts.find(function (n) { return n.id === action.payload.id; });
            if (alert) {
                alert.subject = action.payload.subject;
            }
        },
        setSelectedPrediction: function (state, action) {
            state.selectedPrediction = action.payload;
        },
    },
    extraReducers: function (builder) {
        buildRetrieveModelResultPromiseCases(builder);
        buildUpdateModelThresholdPromiseCases(builder);
    },
});
exports.updateSessionThreshold = (_a = modelResultSlice.actions, _a.updateSessionThreshold), exports.resetSessionThreshold = _a.resetSessionThreshold, exports.setThresholdOptimization = _a.setThresholdOptimization, exports.setAlertSubject = _a.setAlertSubject, exports.setSelectedPrediction = _a.setSelectedPrediction;
exports.default = modelResultSlice.reducer;
//# sourceMappingURL=ModelResultSlice.js.map